import React, {useEffect} from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';

import Button from "../components/CustomButtons/Button.js";
import GridContainer from "../components/Grid/GridContainer.js";
import GridItem from "../components/Grid/GridItem.js";
import Pagination from "../components/Pagination/Pagination";

import styles from "../styles/jss/nextjs-material-kit/pages/articleStyle";

import {useArticlesListRequest} from "../request/api";
import {getPageNumbersToLink} from "../util/pagination";
import {buildSlug} from "../util/url";
import Grid from "@mui/material/Grid";
import {SanitizeHTML} from "../util/string";

const useStyles = makeStyles()(styles);

const ArticleList = ({ article_type, query, category_id = null }) => {
  const { classes, cx } = useStyles();

  const router = useRouter();
  const perPage = 10;
  let currentPageNumber = router.query.page || 1;
  const { data, error, isLoading, mutate } = useArticlesListRequest({
    limit: perPage,
    offset: perPage * (currentPageNumber - 1),
    article_type,
    query,
    category_id,
  });

  useEffect(() => {
    // Update iff the page number changed, to minimise performance impact. We listen explicitly
    // for this because the pagination nav actions are shallow routed to avoid a full page reload.
    // SWR magic should lead the `useArticlesListRequest()` hook to swap in the new page.
    currentPageNumber = router.query.page || 1;
    mutate();
  }, [router.query.page]);

  const pagesToLink = data?.total_count
    ? getPageNumbersToLink(currentPageNumber, Math.ceil(data?.total_count / perPage))
    : [1];

  return (
    <Grid container>
      <Grid item>
        <p className={classes.pageInfo}>
          {data?.articles?.length > 0
            && (<>
              {1 + (currentPageNumber - 1) * perPage}&nbsp;
              to {Math.min(data?.total_count, currentPageNumber * perPage)}&nbsp;
              of {data?.total_count} results
            </>)}
        </p>
      </Grid>

      {!data?.articles?.length && <>
        {isLoading ? <>
          Loading&hellip;
        </> : <>
          No articles found
        </>}
      </>}

      <Grid item container>
        {data?.articles?.length > 0 ? data.articles.map((article, i) => (
          <Grid container
            xs={12}
            sm={12}
            md={12}
            className={cx(classes.lightGrey)}
          >
            <Grid item
              xs={12}
              sm={12}
              md={5}
              className={cx(classes.imgContainer)}
            >
              <img
                src={article.image_uri}
                role="presentation"
                className={classes.imgFluid}
              />
            </Grid>
            <Grid item
              xs={12}
              sm={12}
              md={7}
              className={cx(classes.inlineBlock, classes.padding5)}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h4
                  className={cx(
                    classes.cardTitle,
                    classes.cardTitlePurple
                  )}
                >
                  {article.title}
                </h4>

              </div>

              <p className={classes.cardDesc}>
                <SanitizeHTML html={article?.content_snippet} className="user-content" />
              </p>
              <Button
                color="secondary"
                className={cx(classes.floatLeft, classes.readMoreBtn)}
                component={Link}
                title={`Read more about ${article.title}`}
                href={`/${article_type === 'advice' ? 'advice' : 'news'}/${buildSlug(article.category)}/${buildSlug(article.title)}/${article.id}`}
              >
                Read more
              </Button>
            </Grid>
          </Grid>
        )) : null}
      </Grid>

      {data?.articles?.length > 0 && <>
        <Grid item
          xs={12}
          sm={12}
          md={12}
          className={classes.centred}
        >
          <Pagination
            color="primary"
            pages={pagesToLink.map((page) => {
              const params = {
                page: page,
              };

              if (query) {
                params['q'] = query;
              }

              return {
                active: currentPageNumber == page,
                disabled: false,
                url: router.asPath.split('?')[0] + '?' + new URLSearchParams(params).toString(),
                text: page,
              }
            }, pagesToLink)}
          />
        </Grid>
      </>}
    </Grid>
  );
}

ArticleList.propTypes = {
  article_type: PropTypes.string.isRequired,
}

export default ArticleList;
